import React, { useEffect, useState, useCallback } from "react";
import { Spin, Alert, Table } from "antd";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { People, Repeat, ShoppingCart, AttachMoney } from "@mui/icons-material";
import customClientAxios from "../../customAxios";
import { PlatformAnalyticsCustomers } from "../../utils/ApiEndpoints";
import {
  PageContainer, StatsGrid, StatCard, StatLabel, StatValue, StatSub,
  IconBox, Card, SectionTitle, GRADIENTS, CHART_COLORS, fmtRupees, fmtNum,
} from "../paUtils";
import styled from "styled-components";

const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  margin-bottom: 20px;
`;

const TableHeader = styled.div`
  padding: 20px 24px 16px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
`;

const BUCKET_LABELS = { 1: "1 booking", 2: "2 bookings", 3: "3–4", 5: "5–9", 10: "10–49", 50: "50+" };

function CustomersTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsCustomers, {
        params: { startDate, endDate, limit: 20 },
      });
      setData(res.data.data);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load customers data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const distributionData = (data?.bookingDistribution || []).map((d) => ({
    bucket: BUCKET_LABELS[d._id] || `${d._id}`,
    count: d.count,
  }));

  const topCustomersColumns = [
    {
      title: "Customer",
      key: "customer",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{r.name}</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: "Bookings",
      dataIndex: "bookingCount",
      key: "bookingCount",
      sorter: (a, b) => a.bookingCount - b.bookingCount,
      render: (v) => <span style={{ fontWeight: 700 }}>{fmtNum(v)}</span>,
    },
    {
      title: "Total Spent",
      dataIndex: "totalSpent",
      key: "totalSpent",
      sorter: (a, b) => a.totalSpent - b.totalSpent,
      render: (v) => <span style={{ fontWeight: 700, color: "#10b981" }}>{fmtRupees(v)}</span>,
    },
    {
      title: "Brand Pages",
      dataIndex: "brandPageCount",
      key: "brandPageCount",
      render: (v) => fmtNum(v),
    },
    {
      title: "First Booking",
      dataIndex: "firstBooking",
      key: "firstBooking",
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "-",
    },
    {
      title: "Last Booking",
      dataIndex: "lastBooking",
      key: "lastBooking",
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "-",
    },
  ];

  return (
    <PageContainer>
      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} />}

      <Spin spinning={loading}>
        {data && (
          <>
            <StatsGrid>
              <StatCard>
                <IconBox $bg={GRADIENTS.purple}><People /></IconBox>
                <StatLabel>Total Customers</StatLabel>
                <StatValue>{fmtNum(data.summary?.totalCustomers)}</StatValue>
              </StatCard>
              <StatCard>
                <IconBox $bg={GRADIENTS.blue}><ShoppingCart /></IconBox>
                <StatLabel>Single Booking</StatLabel>
                <StatValue>{fmtNum(data.summary?.singleBooking)}</StatValue>
              </StatCard>
              <StatCard>
                <IconBox $bg={GRADIENTS.green}><Repeat /></IconBox>
                <StatLabel>Repeat Customers</StatLabel>
                <StatValue>{fmtNum(data.summary?.repeatCustomers)}</StatValue>
                <StatSub>
                  {data.summary?.totalCustomers
                    ? `${Math.round((data.summary.repeatCustomers / data.summary.totalCustomers) * 100)}% of total`
                    : ""}
                </StatSub>
              </StatCard>
              <StatCard>
                <IconBox $bg={GRADIENTS.amber}><AttachMoney /></IconBox>
                <StatLabel>Avg Spend</StatLabel>
                <StatValue>{fmtRupees(data.summary?.avgSpend)}</StatValue>
                <StatSub>{data.summary?.avgBookings?.toFixed(1)} avg bookings</StatSub>
              </StatCard>
            </StatsGrid>

            <Card>
              <SectionTitle>Booking Distribution</SectionTitle>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={distributionData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} labelStyle={{ fontWeight: 600, color: "#1e293b" }} />
                  <Bar dataKey="count" name="Customers" fill={CHART_COLORS.gmv} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <TableCard>
              <TableHeader>Top Customers</TableHeader>
              <Table
                dataSource={data.topCustomers || []}
                columns={topCustomersColumns}
                rowKey="customerId"
                size="middle"
                pagination={false}
                scroll={{ x: 700 }}
              />
            </TableCard>
          </>
        )}
      </Spin>
    </PageContainer>
  );
}

export default CustomersTab;
